import { CompositeLayer } from '@deck.gl/core';
import { IconLayer, PathLayer, ScatterplotLayer } from '@deck.gl/layers';

export const CALL_ICONS_LAYER = 'call-icons-layer';
const MOST_RECENT_CALL_CIRCLE_LAYER = 'most-recent-calls-circle-layer';
const HISTORIC_CALL_CIRCLE_LAYER = 'historic-call-circle-layer';
export const SECONDARY_LOCATION_ICON_LAYER = 'secondary-location-icon-layer';
const SECONDARY_LOCATION_PATH_ONE_LAYER = 'secondary-location-path-one-layer';
const SECONDARY_LOCATION_PATH_TWO_LAYER = 'secondary-location-path-two-layer';

class CallsLayer extends CompositeLayer {
  initializeState() {
    super.initializeState();
    this.state.isClicked = false;
  }

  onHover(info, pickingEvent) {
    // This bugfix suppressed the first hover after a click which causes
    // features like popovers to close immediately after a click.
    if (this.state.isClicked) {
      this.state.isClicked = false;
      return;
    }
    super.onHover(info, pickingEvent);
  }

  onClick(info, pickingEvent) {
    this.state.isClicked = true;
    super.onClick(info, pickingEvent);
  }

  renderLayers() {
    const {
      data,
      primaryCallsIconLayerProps,
      mostRecentCircleLayerProps,
      historicCircleLayerProps,
      secondaryCallsIconLayerProps,
      secondaryCallPathLayerProps,
      onHover,
      onClick,
    } = this.props;

    // Primary locations markers
    const primaryCallsIconLayer = new IconLayer(
      this.getSubLayerProps({
        id: CALL_ICONS_LAYER,
        data: data.primaryLocations,
        parameters: {
          depthTest: true,
        },
        onClick,
        onHover,
        ...primaryCallsIconLayerProps,
      }),
    );

    // Most recent location's uncertainty radius
    const mostRecentCircleLayer = new ScatterplotLayer(
      this.getSubLayerProps({
        id: MOST_RECENT_CALL_CIRCLE_LAYER,
        data: mostRecentCircleLayerProps.data,
        parameters: {
          depthTest: false,
        },
        ...mostRecentCircleLayerProps,
      }),
    );

    // Historic locations uncertainty radius
    const historicCircleLayer = new ScatterplotLayer(
      this.getSubLayerProps({
        id: HISTORIC_CALL_CIRCLE_LAYER,
        data: historicCircleLayerProps.data,
        parameters: {
          depthTest: false,
        },
        ...historicCircleLayerProps,
      }),
    );

    // Secondary location marker
    const secondaryCallsIconLayer = new IconLayer(
      this.getSubLayerProps({
        id: SECONDARY_LOCATION_ICON_LAYER,
        data: data.secondaryLocations,
        onClick,
        onHover,
        ...secondaryCallsIconLayerProps,
      }),
    );

    let secondaryCallPathOneLayer;
    let secondaryCallPathTwoLayer;

    if (data.primaryLocations.length && data.secondaryLocations.length) {
      // only draw when both Primary and Secondary Location is truthy
      // Overlapping PathLayers for the line between primary and secondary location

      secondaryCallPathOneLayer = new PathLayer(
        this.getSubLayerProps({
          id: SECONDARY_LOCATION_PATH_ONE_LAYER,
          data: secondaryCallPathLayerProps.data,
          widthMaxPixels: 2,
          widthMinPixels: 2,
          getWidth: () => 2,
          getColor: () => [0, 191, 136, 255], // green
          getPath: d => {
            return d;
          },
          ...secondaryCallPathLayerProps,
        }),
      );

      secondaryCallPathTwoLayer = new PathLayer(
        this.getSubLayerProps({
          id: SECONDARY_LOCATION_PATH_TWO_LAYER,
          data: secondaryCallPathLayerProps.data,
          widthMaxPixels: 6,
          widthMinPixels: 6,
          getWidth: () => 6,
          getColor: () => [0, 0, 0, 77], // black
          getPath: d => {
            return d;
          },
          ...secondaryCallPathLayerProps,
        }),
      );
    }

    // CircleLayers returned first in order to be rendered under the IconLayers
    // and provide depth testing
    return [
      secondaryCallPathOneLayer,
      secondaryCallPathTwoLayer,
      mostRecentCircleLayer,
      historicCircleLayer,
      primaryCallsIconLayer,
      secondaryCallsIconLayer,
    ];
  }
}

CallsLayer.layerName = 'CallsLayer';
CallsLayer.defaultProps = { visible: true, data: [] };

/**
 * This layer is the same as IconPolygonLayer, but the order of the layers is
 * reversed. This is used by the DeckOverlay to reverse layer order for map
 * providers that have different ordering conventions (like GMaps).
 */
export class CallsLayerReversed extends CallsLayer {
  renderLayers() {
    const layers = super.renderLayers();
    return layers.reverse();
  }
}

export default CallsLayer;
